/**
 * PDP Related Products – Algolia related-products carousel.
 * Replaces server-side Shopify native recommendations with Algolia results.
 */

const APP_ID = '18TT9N4CSD';
const API_KEY = '13f15ea1fb4b1badcdcb224b64a1fc69';
const INDEX = 'shopify_products';

function xhrPost(url, headers, body) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.onload = () => {
      try { resolve(JSON.parse(xhr.responseText)); } catch (e) { reject(e); }
    };
    xhr.onerror = () => reject(new Error('XHR failed'));
    xhr.send(body);
  });
}

function xhrGet(url) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.onload = () => {
      try { resolve(JSON.parse(xhr.responseText)); } catch (e) { reject(e); }
    };
    xhr.onerror = () => reject(new Error('XHR failed'));
    xhr.send();
  });
}

const ALGOLIA_HEADERS = {
  'X-Algolia-Application-Id': APP_ID,
  'X-Algolia-API-Key': API_KEY,
  'Content-Type': 'application/json',
};
const ALGOLIA_URL = `https://${APP_ID}-dsn.algolia.net/1/indexes/*/recommendations`;
const SEARCH_URL = `https://${APP_ID}-dsn.algolia.net/1/indexes/${INDEX}/query`;

async function getObjectId(productId) {
  const data = await xhrPost(
    SEARCH_URL,
    ALGOLIA_HEADERS,
    JSON.stringify({ query: '', filters: `id:${productId}`, hitsPerPage: 1, attributesToRetrieve: ['objectID'] })
  );
  return data.hits?.[0]?.objectID ?? null;
}


function renderCard(product) {
  const price = (product.price / 100).toFixed(2);
  const comparePrice = product.compare_at_price ? (product.compare_at_price / 100).toFixed(2) : null;
  const image = product.featured_image
    ? product.featured_image.replace(/^\/\//, 'https://')
    : '';
  const priceHtml = comparePrice && parseFloat(comparePrice) > parseFloat(price)
    ? `<s class="native-related-products__compare">${comparePrice}</s> <span class="native-related-products__current">${price}</span>`
    : `<span class="native-related-products__current">${price}</span>`;
  const firstVariant = product.variants?.[0];
  const variantId = firstVariant?.id ?? '';
  const available = product.available;

  return `
    <div class="native-related-products__card">
      <a href="${product.url}" class="native-related-products__img-wrap">
        ${image ? `<img src="${image}&width=300" alt="${product.title}" loading="lazy" width="300" height="300">` : ''}
      </a>
      <div class="native-related-products__info">
        <a href="${product.url}" class="native-related-products__title-link">
          <p class="native-related-products__title">${product.title}</p>
        </a>
        <p class="native-related-products__price">${priceHtml}</p>
      </div>
      ${available && variantId
        ? `<button class="native-related-products__btn" data-add-variant="${variantId}">Add to cart</button>`
        : `<button class="native-related-products__btn native-related-products__btn--oos" disabled>Sold out</button>`
      }
    </div>`;
}

async function run(section) {
  const productId = section.dataset.productId;
  const currentHandle = section.dataset.productHandle;
  const limit = parseInt(section.dataset.limit, 10) || 3;
  const isArabic = document.documentElement.lang === 'ar' || window.location.pathname.startsWith('/ar/');
  const locale = isArabic ? '/ar' : '';

  // 1. Use objectID from data attribute if available, otherwise resolve via Search
  const objectId = section.dataset.algoliaObjectId || await getObjectId(productId);

  const cache = window.__algoliaRecommendCache;
  const cacheKey = ['related', objectId || productId];
  const cached = cache?.get(cacheKey);

  let hits;
  if (cached) {
    hits = cached;
  } else {
    // 2. Batch related + trending in a single POST to minimize API calls
    const requests = [];
    if (objectId) {
      requests.push({
        indexName: INDEX,
        model: 'related-products',
        objectID: objectId,
        maxRecommendations: limit,
        threshold: 0,
        queryParameters: {
          attributesToRetrieve: ['objectID', 'title', 'handle', 'price', 'compare_at_price', 'image', 'id'],
          filters: 'inventory_available:true',
        },
      });
    }

    if (!requests.length) {
      section.style.display = 'none';
      return;
    }

    const data = await xhrPost(ALGOLIA_URL, ALGOLIA_HEADERS, JSON.stringify({ requests }));
    const results = data.results || [];

    let relatedHits = results[0]?.hits || [];
    let trendingHits = [];

    hits = [...relatedHits];
    if (hits.length < 3) {
      const existingIds = new Set(hits.map((h) => h.objectID));
      for (const h of trendingHits) {
        if (!existingIds.has(h.objectID)) hits.push(h);
        if (hits.length >= limit) break;
      }
    }
    cache?.set(cacheKey, hits);
  }

  // 3. Exclude current product
  hits = hits.filter((h) => h.handle !== currentHandle).slice(0, limit);
  if (hits.length === 0) return;

  // 4. Fetch full product data (for locale-aware titles + image)
  const products = await Promise.all(
    hits.map((h) =>
      xhrGet(`${locale}/products/${h.handle}.js`).catch(() => null)
    )
  );

  // 5. Render cards
  const track = section.querySelector('[data-track]');
  if (!track) return;

  track.innerHTML = products
    .filter(Boolean)
    .map(renderCard)
    .join('');

  // Mark as loaded
  section.setAttribute('data-algolia-loaded', '');

  // Init carousel arrows
  const prev = section.querySelector('[data-arrow-prev]');
  const next = section.querySelector('[data-arrow-next]');

  const updateArrows = () => {
    const { scrollLeft, scrollWidth, clientWidth } = track;
    if (prev) prev.hidden = scrollLeft <= 2;
    if (next) next.hidden = scrollLeft + clientWidth >= scrollWidth - 2;
  };

  const getScrollAmount = () => {
    const card = track.querySelector('.native-related-products__card');
    if (!card) return track.clientWidth * 0.75;
    const gap = parseFloat(getComputedStyle(track).gap) || 16;
    return (card.offsetWidth + gap) * 2;
  };

  if (prev) {
    prev.addEventListener('click', () => track.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' }));
  }
  if (next) {
    next.addEventListener('click', () => track.scrollBy({ left: getScrollAmount(), behavior: 'smooth' }));
  }

  track.addEventListener('scroll', updateArrows, { passive: true });
  new ResizeObserver(updateArrows).observe(track);
  updateArrows();

  // ATC buttons
  for (const btn of track.querySelectorAll('[data-add-variant]')) {
    btn.addEventListener('click', () => {
      const variantId = btn.dataset.addVariant;
      if (!variantId || btn.disabled) return;
      btn.disabled = true;
      const original = btn.textContent;
      btn.textContent = btn.dataset.addingText || 'Adding…';
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/cart/add.js');
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          btn.textContent = btn.dataset.addedText || 'Added ✓';
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } else {
          btn.textContent = original;
          btn.disabled = false;
        }
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 2000);
      };
      xhr.onerror = () => { btn.textContent = original; btn.disabled = false; };
      xhr.send(JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }));
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const section = document.querySelector('[data-pdp-related]');
  if (section) run(section).catch((e) => console.warn('[pdp-related]', e));
});
